import { useTranslation } from "react-i18next";
import ITicket from "../../../models/helpdesk/it/ITicket";
import { Alert, Badge, Card, Col, Container, Nav, OverlayTrigger, Row, Tab, Tooltip } from "react-bootstrap";
import { Icon } from "../../../components/icon/Icon";
import BackButton from "../../../components/backButton/BackButton";
import DetailsTab from "./tabs/details/DetailsTab";
import TicketTab from "../../ticketTab/TicketTab";
import { MessageTab } from "../../common/tabs/comms/MessageTab";
import WorkTab from "./tabs/work/WorkTab";
import AttachmentsTab from "../../common/tabs/attachments/AttachmentsTab";
import { FeedbackType } from "../../../enums/FeedbackType";
import IFeedback from "../../../models/helpdesk/it/IFeedback";

export default function TicketBody({ ticket }: { ticket: ITicket }) {
    const { t } = useTranslation();

    return (
        <Card.Body>
            <Card.Title className="d-flex align-items-center mb-3">
                <BackButton path="/it" />
                <span>{ticket.ticketNumber}</span>
            </Card.Title>
            <Card.Text as="div">
                <Container fluid>
                    <Row>
                        <Col>
                            <h1 className="h2">{ticket.title}</h1>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <div className="d-flex gap-2 mb-3 w-100 overflow-x-auto">
                                <OverlayTrigger placement="top" overlay={<Tooltip>{t("ticket.badges.status")}</Tooltip>}>
                                    <Badge bg="primary" className="fw-normal">
                                        <Icon iconName="Clock" endMargin={1} />
                                        <span className="mb-0">{ticket.status!.name}</span>
                                    </Badge>
                                </OverlayTrigger>
                                {
                                    ticket.assignee?.name &&
                                    <OverlayTrigger placement="top" overlay={<Tooltip>{t("ticket.badges.assignee")}</Tooltip>}>
                                        <Badge bg="info" className="fw-normal">
                                            <Icon iconName="Person" endMargin={1} />
                                            <span className="mb-0">{ticket.assignee!.name}</span>
                                        </Badge>
                                    </OverlayTrigger>
                                }
                            </div>
                        </Col>
                    </Row>
                    {
                        ticket.feedback &&
                        <FeedbackAlert feedback={ticket.feedback!} />
                    }
                    <Row>
                        <Col>
                            <Tab.Container defaultActiveKey="info">
                                <Row>
                                    <Col xs={12} lg={3} xl={2}>
                                        <Nav variant="pills" className="flex-lg-column">
                                            <TicketTab eventKey="info" icon="InfoCircleFill" label={t("it.ticketTabs.details")} />
                                            <TicketTab eventKey="comms" icon="ChatDots" label={t("comms.tabName")} />
                                            <TicketTab eventKey="work" icon="Clipboard2Check" label={t("it.ticketTabs.work")} />
                                            <TicketTab eventKey="attachments" icon="Paperclip" label={t("attachments.title")} />
                                        </Nav>
                                    </Col>
                                    <Col sm={9}>
                                        <Tab.Content>
                                            <Tab.Pane eventKey="info">
                                                <DetailsTab ticket={ticket} />
                                            </Tab.Pane>
                                            <Tab.Pane eventKey="work">
                                                <WorkTab />
                                            </Tab.Pane>
                                            <Tab.Pane eventKey="comms">
                                                <MessageTab ticket={ticket} />
                                            </Tab.Pane>
                                            <Tab.Pane eventKey="attachments">
                                                <AttachmentsTab ticket={ticket} />
                                            </Tab.Pane>
                                        </Tab.Content>
                                    </Col>
                                </Row>
                            </Tab.Container>
                        </Col>
                    </Row>
                </Container>
            </Card.Text>
        </Card.Body>
    );
};

const FeedbackAlert = ({ feedback }: { feedback: IFeedback }) => {
    const { t } = useTranslation();
    
    const icon = feedback.type === FeedbackType.POSITIVE
        ? "EmojiSmileFill"
        : "EmojiFrownFill";

    const message = feedback.type === FeedbackType.POSITIVE
        ? t("it.feedback.positive")
        : t("it.feedback.negative");
        
    return (
        <Alert variant="light" className="d-flex align-items-center gap-2">
            <div className="me-3">
                <span className="display-6">
                    <Icon iconName={icon} />
                </span>
            </div>
            <div className="d-block">
                <p className="lead mb-1">
                    {message}
                </p>
                <div dangerouslySetInnerHTML={{ __html: t("it.feedback.yourMessage", { message: feedback.message }) }} />
            </div>
        </Alert>
    );
};